import {AfterViewInit, Directive, ElementRef, HostListener, Input, Renderer2,} from '@angular/core';
import {NgControl,} from '@angular/forms';
import {MatDateFormats,} from '@angular/material/core';
import * as moment from 'moment';

@Directive({
  selector: '[yearMonthDayInput]',
})
export class YearMonthDayInputDirective implements AfterViewInit {
  @Input() isDay27Only = false;
  validYear = 0;
  validMonth = '';
  validDay = '';
  keys: string[] = [];
  dateEKeypress = '';
  dateEInput = '';
  dateEUpDown = '';
  groupChanged = '';
  groupChangedForUpDown = '';
  now = '';
  isDashDisplay = false;
  isSkipSetDefault = false;
  isFocus = false;
  isClick = false;
  min = '1753-01-01';
  minValidYear = 1753;
  max = '9998-12-31';
  maxValidYear = 9998;
  maxYYYInput = 999;

  constructor(private el: ElementRef, private dateElement: NgControl, private renderer: Renderer2) {
    const matches = (new Date()).toLocaleDateString().match(/\d{4}\/\d{1,2}\/\d{1,2}/g);
    if (matches && matches.length > 0) {
      this.isDashDisplay = true;
    }

    this.now = this.getCurrentYMD();
  }

  ngAfterViewInit(): void {
    // デフォルトでは、年-月-日を入力する場合、最大6桁まで年を入力できる。
    // ユーザーが入力できる最大値を 9999に制限する為に最小/最大を設定する。
    const minAttr = this.el.nativeElement.getAttribute('min');
    const maxAttr = this.el.nativeElement.getAttribute('max');
    if (minAttr) {
      const minMt = moment(minAttr);
      this.min = minMt.format('YYYY-MM-DD');
      this.minValidYear = Number(minMt.format('YYYY'));
    }
    if (maxAttr) {
      const maxMt = moment(maxAttr);
      this.max = maxMt.format('YYYY-MM-DD');
      this.maxValidYear = Number(maxMt.format('YYYY'));
      this.maxYYYInput = Number(maxMt.format('YYYY').substring(0, 3));
    }
    this.renderer.setAttribute(this.el.nativeElement, 'min', this.min);
    this.renderer.setAttribute(this.el.nativeElement, 'max', this.max);
    if (this.isDashDisplay) {
      this.renderer.addClass(this.el.nativeElement, 'input-ymd');
    }
    this.updateCSS();

    this.dateElement.valueChanges?.subscribe((value) => {
      if (!value && (this.keys[this.keys.length - 1] === 'Delete' || !this.isFocus)) {
        // ユーザがDeleteキーを押下するか、画面上のクリアボタンを押下する場合、バックアップした有効な年-月-日を削除する。
        this.validYear = 0;
        this.validMonth = '';
        this.validDay = '';
      }
      this.dateElement.control?.markAsDirty();
      this.updateCSS();
    });

    const value = this.dateElement?.control?.value;
    if (value && moment(value).isValid()) {
      this.dateElement.control?.markAsDirty();
    }
  }

  @HostListener('focusin', ['$event',]) onFocusin(): void {
    if (this.el.nativeElement.type !== 'date') {
      return;
    }
    this.isFocus = true;
    const values = this.el.nativeElement.value.split('-');
    this.validYear = Number(values[0]);
    this.validMonth = values[1];
    this.validDay = values[2];
  }

  @HostListener('keydown', ['$event',]) onKeyDown(event: KeyboardEvent): void {
    if (this.el.nativeElement.type !== 'date') {
      return;
    }
    this.isClick = false;
    if (this.keys[this.keys.length - 1] === 'Shift' && event.key === 'Shift') {
      // Shiftキーを押下した為、連続してFireした場合に処理しない。
    } else if (this.keys[this.keys.length - 1] === '0' && event.key === '0') {
      // 0キーを連続して押下した場合に処理しない。
    } else {
      // 最後に押下された5つのキーのみを保存する。
      this.keys.push(event.key);
      if (this.keys.length > 5) {
        this.keys = this.keys.slice(2);
      }
    }
    const numbers = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9',];
    if (event.key === 'Tab' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      // Tab、Left、Rightのキーを押下すると、日付が再度フォーマットされる。
      // 値が「XXXX-02-29」で、「XXXX」がうるう年ではない場合、日付が再度フォーマットされない。
      if (!(this.validMonth === '02' && this.validDay === '29' && !this.el.nativeElement.value)) {
        this.updateValue();
      }
      this.resetKeys();
      this.groupChangedForUpDown = event.key === 'ArrowLeft' ? 'year' : 'day';
      return;
    } else if (event.key === 'Delete') {
      this.dateElement?.control?.setValue('');
      event.preventDefault();
      this.resetKeys();
      return;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      this.dateEUpDown = this.el.nativeElement.value;
      this.keys = this.keys.filter((key, index) =>
        (key !== 'ArrowUp' && key !== 'ArrowDown') || index === this.keys.length - 1);
    } else if (numbers.indexOf(event.key) === -1) {
      // 無効な文字を入力できない。
      event.preventDefault();
      return;
    }
    if (this.isSkipSetDefault) {
      this.isSkipSetDefault = false;
    } else if (!this.el.nativeElement.value) {
      if (this.validYear && this.validMonth && this.validDay) {
        // 入力された年がうるう年ではなく、以前の値が「XXXX-02-29」である場合、引き続き年を入力できるように項目の値を再設定しないようにする。
        if (this.validMonth === '02' && this.validDay === '29') {
          // うるう年ではない旨を通知するエラーメッセージが表示される。
          this.dateElement?.control?.setErrors({invalidLeapYear: true,});
        } else {
          this.dateElement?.control?.setValue(this.validYear + '-' + this.validMonth + '-' + this.validDay);
        }
      } else {
        this.dateElement?.control?.setValue(this.now);
      }
      this.updateCSS();
    }
    // 有効な年を保存する。
    const newYear = Number(this.el.nativeElement.value.replace(/-\d+/g, ''));
    if (!isNaN(newYear) && newYear >= this.minValidYear && newYear <= this.maxValidYear) {
      this.validYear = newYear;
    }
  }

  @HostListener('keypress', ['$event',]) onKeypress(event: KeyboardEvent): void {
    if (this.el.nativeElement.type !== 'date') {
      return;
    }
    if (!this.el.nativeElement.value) {
      this.isSkipSetDefault = true;
      return;
    }
    this.dateEKeypress = this.el.nativeElement.value;
    this.dateEUpDown = this.el.nativeElement.value;
    const values = this.el.nativeElement.value.split('-');
    this.validMonth = values[1];
    this.validDay = values[2];
    if (this.groupChanged === 'month') {
      // 月を入力した場合に処理する。
      const newMonth = values[1];
      if (newMonth === '12' && this.keys[this.keys.length - 2] === '0') {
        // 12月を受け取り、ユーザが0を入力した場合
        this.updateValue();
      } else if (this.keys[this.keys.length - 2] === '1' && (this.keys[this.keys.length - 1] === '1' || this.keys[this.keys.length - 1] === '2')) {
        // ユーザが11月、12月を入力した場合
        this.updateValue();
      } else if (Number(this.keys[this.keys.length - 2] + this.keys[this.keys.length - 1]) > 12) {
        // ユーザが12月の未来月を入力した場合
        if (this.keys[this.keys.length - 1] === '0') {
          event.preventDefault();
          return;
        }
        this.updateValue();
      } else if (Number(this.keys[this.keys.length - 2] + this.keys[this.keys.length - 1]) > 9) {
        // ユーザが10月、11月、12月を入力した場合
        this.updateValue();
      }
    }
    if (this.groupChanged === 'day') {
      // 日を入力した場合に処理する。
      const maxDay = (new Date(values[0], values[1], 0)).getDate();
      if (this.keys[this.keys.length - 1] === '0') {
        // ユーザが日に0を入力する場合
        // 10日、20日、30日を入力する場合、入力可能とする。
        // 40日、50日等を入力する場合、処理を終了する。
        if (Number(this.keys[this.keys.length - 2]) > 3) {
          this.keys.pop();
          event.preventDefault();
          return;
        }
        this.validDay = this.keys[this.keys.length - 2] + this.keys[this.keys.length - 1];
      } else if (Number(this.keys[this.keys.length - 2] + this.keys[this.keys.length - 1]) > maxDay) {
        // ユーザが月の許可日よりも後日を入力する場合、日を訂正する。
        // 例：2022/03/34を入力する場合、2022/03/04に変換する。
        this.updateValue();
        event.preventDefault();
      }
    }
  }

  @HostListener('input', ['$event',]) onInput(event: InputEvent): void {
    if (this.el.nativeElement.type !== 'date') {
      return;
    }
    if (!this.el.nativeElement.value) {
      event.preventDefault();
      return;
    }
    this.dateEInput = this.el.nativeElement.value;
    const prevDate = this.dateEKeypress.split('-');
    const nextDate = this.dateEInput.split('-');
    const newYear = Number(nextDate[0]);
    if (prevDate[0] !== nextDate[0]) {
      // ユーザが年を入力していると判断する。
      this.groupChanged = 'year';
    }
    if (prevDate[1] !== nextDate[1]) {
      // ユーザが月を入力していると判断する。
      this.groupChanged = 'month';
    }
    if (prevDate[2] !== nextDate[2]) {
      // ユーザが日を入力していると判断する。
      this.groupChanged = 'day';
    }
    if (!isNaN(newYear) && newYear >= this.minValidYear && newYear <= this.maxValidYear) {
      this.validYear = newYear;
    }
    if (newYear > this.maxYYYInput) {
      this.updateValue();
    }

    // ArrowUp、ArrowDownの押下用の変更対象部分判定処理
    const setDate = this.dateEUpDown.split('-');
    if (nextDate[0] !== setDate[0]) {
      this.groupChangedForUpDown = 'year';
    } else if (nextDate[1] !== setDate[1]) {
      this.groupChangedForUpDown = 'month';
    } else if (nextDate[2] !== setDate[2]) {
      this.groupChangedForUpDown = 'day';
    } else {
      this.groupChangedForUpDown = '';
    }
  }

  @HostListener('focusout', ['$event',]) onFocusout(): void {
    if (this.el.nativeElement.type !== 'date') {
      return;
    }
    this.updateValue();
    this.isFocus = false;
    this.groupChangedForUpDown = '';
  }

  @HostListener('click', ['$event',]) onClick(event: MouseEvent): void {
    if (this.el.nativeElement.type !== 'date') {
      return;
    }
    this.isClick = true;
    this.updateValue();
    // ユーザーが入力している年月日の値をマウスで変更する場合、groupChangedとkeysをリセットする。
    this.resetKeys();

    if (event.offsetX < 50) {
      // 年分をクリックした時
      this.groupChangedForUpDown = 'year';
    } else if (event.offsetX > 95) {
      // 右の空白文をクリックした時
      this.groupChangedForUpDown = this.groupChangedForUpDown ? 'day' : 'year';
    } else {
      // 日分をクリックした時
      this.groupChangedForUpDown = 'day';
    }
  }

  @HostListener('dateChange', ['$event',]) onDateChange(): void {
    if (this.el.nativeElement.type !== 'date') {
      return;
    }

    // 上下キーを押下した場合の制御
    if (['ArrowUp', 'ArrowDown',].includes(this.keys[this.keys.length - 1])) {
      // 無効な値を入力した場合の制御
      // 入力値が有効な場合、制御処理が不要
      if (!this.el.nativeElement.value) {
        let setYear = this.validYear
        let setMonth = Number(this.validMonth);
        let setDay = Number(this.validDay);

        if (this.validDay === '31') {
          // 31日のある月から30日以下の月に変更するとき、その月の最大日数に合わせて制御
          setMonth = this.keys[this.keys.length - 1] === 'ArrowUp' ?
            Number(this.validMonth) + 1 : Number(this.validMonth) - 1;
          setDay = (new Date(setYear, setMonth, 0)).getDate();
        } else if (this.validDay === '01' && this.keys[this.keys.length - 1] === 'ArrowDown') {
          // 30日以下の月で01日からArrowDownを押下したら、その月の最大日数に回す
          setDay = (new Date(setYear, setMonth, 0)).getDate();
        } else if (this.validMonth === '01' && this.keys[this.keys.length - 1] === 'ArrowUp') {
          // 01月29~31日から02月に変更するとき、02月の最大日数に変更
          setMonth = 2
          setDay = (new Date(setYear, setMonth, 0)).getDate();
        } else if (this.validMonth === '03' && this.keys[this.keys.length - 1] === 'ArrowDown') {
          // 03月29~31日から02月に変更するとき、02月の最大日数に変更
          setMonth = 2
          setDay = (new Date(setYear, setMonth, 0)).getDate();
        } else if (['28', '30',].includes(this.validDay) && this.keys[this.keys.length - 1] === 'ArrowUp') {
          // 28日か30日のみの月で最大日数からArrowUpを押下したら、01日に回す
          setDay = 1;
        } else if (this.validMonth === '02' && this.validDay === '29' && this.keys[this.keys.length - 1] === 'ArrowDown') {
          // 02月29日から年をさげるときに、02月28日に変更
          setYear = this.validYear - 1;
          setDay = (new Date(setYear, setMonth, 0)).getDate();
        } else if (this.validMonth === '02' && this.validDay === '29' && this.keys[this.keys.length - 1] === 'ArrowUp') {
          // 02月29日から年をあげるときに、02月28日に変更または、最大日数からArrowUpを押下したら、01日に回す
          setYear = this.groupChangedForUpDown === 'day' ? this.validYear : this.validYear + 1;
          setDay = this.groupChangedForUpDown === 'day' ? 1 : (new Date(setYear, setMonth, 0)).getDate();
        }

        this.validYear = setYear;
        this.validMonth = setMonth.toString().padStart(2, '0');
        this.validDay = setDay.toString().padStart(2, '0');

        // 有効な日付に設定
        this.dateElement?.control?.setValue(this.validYear + '-'
          + setMonth.toString().padStart(2, '0') + '-' + setDay.toString().padStart(2, '0'));
      }

      // 最新のArrowUpまたはArrowDownキー押下を外し、次の押下に準備
      this.keys = this.keys.slice(0, this.keys.length - 1);
    }

    if (!this.el.nativeElement.value && this.keys.length >= 2) {
      const value = this.keys[this.keys.length - 2] + this.keys[this.keys.length - 1];
      const maxDay = (new Date(this.validYear, Number(this.validMonth), 0)).getDate();
      if ((this.validDay === '29' || this.validDay === '30' || this.validDay === '31') && value !== '00') {
        // ユーザーが2022/08/31を1回目入力し、
        // 次に、8月から2月までの日付を入力した場合⇒02/31が無効である。
        // この時、有効な日付が2月の最終日に変換される。
        if (this.validMonth === '02' && this.validDay === '29') {
          // うるう年ではない旨を通知するエラーメッセージが表示される。
          this.dateElement?.control?.setErrors({invalidLeapYear: true,});
          // 有効な日となる8888/02/29を入力してから、年を8888から8887に変更した場合、8887/02/28にリセットしない。
          return;
        }
        if (this.validDay === value) {
          // ユーザがvalidDayと同じ日を入力する場合、年月日を変更しない。
          this.dateElement?.control?.setValue(this.validYear + '-' + this.validMonth + '-' + this.validDay);
        } else if (Number(value) > maxDay) {
          // ユーザが日を0と入力する、年月日を変更しない。
          this.dateElement?.control?.setValue(this.validYear + '-' + this.validMonth + '-' + this.validDay);
        } else if (Number(value) > 0) {
          // ユーザが月を変更する場合、日を月の最大日にセットする。
          if (this.keys[this.keys.length - 1] !== '0') {
            if (Number(value) > 12) {
              this.validMonth = '0' + this.keys[this.keys.length - 1];
            } else {
              this.validMonth = value.replace(' ', '0');
            }
            this.validDay = String((new Date(this.validYear, Number(this.validMonth), 0)).getDate());
            this.dateElement?.control?.setValue(this.validYear + '-' + this.validMonth + '-' + this.validDay);
            this.resetKeys();
            setTimeout(() => { this.el.nativeElement.focus(); }, 100);
          }
        } else {
          // ユーザが月を0と入力する、年月日を変更しない。
          this.dateElement?.control?.setValue(this.validYear + '-' + this.validMonth + '-' + this.validDay);
        }
      } else {
        // ユーザーが31より後日を入力した場合、最後に入力された値を受け取る。
        // たとえば、ユーザーが32日を入力すると、日付ピッカーで2日を受け取る。
        if (Number(value) > maxDay) {
          // ユーザが1ヶ月で許可されている日付より後の日付を入力する（例えば2022/03/40を入力する）場合、日セルの値をリセットする。
          if (this.keys[this.keys.length - 1] === '0') {
            // 0を入力した場合、直前の入力値を取得する。
            if (Number(this.keys[this.keys.length - 3] + this.keys[this.keys.length - 2]) <= maxDay) {
              // ユーザが有効な日を入力してから、0を入力する場合、validDayをリセットしない。
            } else if (this.validMonth === ('0' + this.keys[this.keys.length - 2])) {
              // ユーザが有効な月を入力する場合、日時ピッカで日セルに遷移し、月の値と同じ日を入力すると、validDayをリセットしない。
            } else {
              this.validDay = '0' + this.keys[this.keys.length - 2];
            }
          } else {
            // 0以外を入力した場合、
            this.validDay = '0' + this.keys[this.keys.length - 1];
          }
        } else if (Number(value) > 0 && this.keys[this.keys.length - 1] !== '0') {
          this.validDay = value;
        }
        this.dateElement?.control?.setValue(this.validYear + '-' + this.validMonth + '-' + this.validDay);
      }
    } else if (!this.el.nativeElement.value && this.keys.length === 1) {
      // ユーザが無効な日、0を入力する場合、年月日を変更しない。
    } else {
      // ユーザが有効な年月日を入力した。
      if (this.groupChanged === 'year') {
        this.keys.push(' ');
      }
      this.groupChanged = '';
    }
    this.updateCSS();
  }

  private updateValue(): void {
    const currentValidYMD = this.validYear + '-' + this.validMonth + '-' + this.validDay;
    if (currentValidYMD === this.el.nativeElement.value) {
      return;
    }
    if (!this.el.nativeElement.value) {
      if (this.keys[this.keys.length - 1] === 'Tab' && this.keys[this.keys.length - 2] === '0' && this.groupChanged === '') {
        this.validYear = 2000;
      }
      if (this.validDay && this.validMonth && this.validYear > 0) {
        this.dateElement?.control?.setValue(this.validYear + '-' + this.validMonth + '-' + this.validDay);
      }
      return;
    }
    let values = this.el.nativeElement.value.split('-');
    let newYear = Number(values[0]);
    let newMonth = values[1];
    let newDay = values[2];
    let isInput00 = false;
    if (this.groupChanged === '' && this.keys[this.keys.length - 1] === ' ') {
      this.keys.pop();
      this.groupChanged = 'year';
    }
    if (this.groupChanged === 'year' && !this.isClick) {
      // 年を入力した場合に処理する。
      if (this.keys[this.keys.length - 1] === '0' && newYear === 1) {
        newYear = 0;
      }
      if (this.keys[this.keys.length - 1] === 'Tab' && this.keys[this.keys.length - 2] === '0' && newYear === 1) {
        newYear = 0;
      }
      if (this.keys[this.keys.length - 1] === 'Tab' && this.keys[this.keys.length - 2] === 'Shift' && this.keys[this.keys.length - 3] === '0' && newYear === 1) {
        newYear = 0;
      }
    }
    if (this.groupChanged === 'month' && !this.isClick) {
      // 月を入力した場合に処理する。
      const prevDate = this.dateEKeypress.split('-');
      if (this.keys[this.keys.length - 2] === '1' && this.keys[this.keys.length - 1] === '0' && (newMonth === '01' || newMonth === '11')) {
        newMonth = '10';
      } else if (this.keys[this.keys.length - 2] === '1' && this.keys[this.keys.length - 1] === '1' && newMonth === '01') {
        newMonth = '11';
      } else if (this.keys[this.keys.length - 2] === '1' && this.keys[this.keys.length - 1] === '2' && newMonth === '02') {
        newMonth = '12';
      } else if (this.keys[this.keys.length - 1] === '0' && newMonth === '01' && prevDate.length > 1) {
        newMonth = prevDate[1];
        isInput00 = true;
      }
      if (this.keys[this.keys.length - 1] === 'Tab' && this.keys[this.keys.length - 2] === '0' && newMonth === '01' && prevDate.length > 1) {
        newMonth = prevDate[1];
      }
      if (this.keys[this.keys.length - 1] === 'ArrowLeft' && this.keys[this.keys.length - 2] === '0' && newMonth === '01' && prevDate.length > 1) {
        newMonth = prevDate[1];
      }
      if (this.keys[this.keys.length - 1] === 'Tab' && this.keys[this.keys.length - 2] === 'Shift' && this.keys[this.keys.length - 3] === '0' && newMonth === '01' && prevDate.length > 1) {
        newMonth = prevDate[1];
      }
      if (this.keys[this.keys.length - 1] === '2' && this.keys[this.keys.length - 2] === '1' && newMonth === '12') {
        // その直前の有効な値が12で、ユーザがもう12回連続して入力した場合、処理しない。
      } else if (newMonth === '12' && Number(this.keys[this.keys.length - 2] + this.keys[this.keys.length - 1]) > 12) {
        newMonth = '0' + this.keys[this.keys.length - 1];
      } else if (newMonth === '12' && this.keys[this.keys.length - 2] === '0') {
        newMonth = '0' + this.keys[this.keys.length - 1];
      }
      if (newMonth === '00' || newMonth.length > 2) {
        newMonth = this.validMonth;
      }
    }
    if (this.groupChanged === 'day') {
      // 日を入力した場合に処理する。
      const prevDate = this.dateEKeypress.split('-');
      if (this.keys[this.keys.length - 1] === 'Tab' && this.keys[this.keys.length - 2] === '0' && newDay === '01') {
        newDay = prevDate[2];
      }
      if (this.keys[this.keys.length - 1] === 'ArrowLeft' && this.keys[this.keys.length - 2] === '0' && newDay === '01') {
        newDay = prevDate[2];
      }
      if (this.keys[this.keys.length - 1] === 'Tab' && this.keys[this.keys.length - 2] === 'Shift' && this.keys[this.keys.length - 3] === '0' && newDay === '01') {
        newDay = prevDate[2];
      }
      if (this.keys[this.keys.length - 1] === '0' && newDay === '01') {
        newDay = prevDate[2];
        isInput00 = true;
      }
      const maxDay = (new Date(newYear, Number(newMonth), 0)).getDate();
      if (Number(this.keys[this.keys.length - 2] + this.keys[this.keys.length - 1]) > maxDay) {
        newDay = '0' + this.keys[this.keys.length - 1];
      }
      if (newDay === '00') {
        if (this.validDay) {
          newDay = this.validDay;
        } else {
          newDay = '0' + this.keys[this.keys.length - 2];
        }
      }
    }

    // デートピッカに有効な値を設定する。
    let rYear = -1;
    if (newYear >= 10 && newYear < 30) {
      rYear = Number('20' + newYear);
    } else if (newYear >= 0 && newYear < 10) {
      rYear = Number('200' + newYear);
    } else if (newYear >= 30 && newYear < 100) {
      rYear = Number('19' + newYear);
    }
    if (rYear > 0 && (rYear > this.maxValidYear || rYear < this.minValidYear)) {
      newYear = this.validYear;
    }
    if (newYear >= 9999 || newYear > this.maxValidYear) {
      this.dateElement?.control?.setValue(this.validYear + '-' + newMonth + '-' + newDay);
    } else if (newYear >= 10 && newYear < 30) {
      this.dateElement?.control?.setValue(Number('20' + newYear) + '-' + newMonth + '-' + newDay);
    } else if (newYear >= 0 && newYear < 10) {
      this.dateElement?.control?.setValue(Number('200' + newYear) + '-' + newMonth + '-' + newDay);
    } else if (newYear >= 30 && newYear < 100) {
      this.dateElement?.control?.setValue(Number('19' + newYear) + '-' + newMonth + '-' + newDay);
    } else if (newYear >= 100 && newYear < this.minValidYear) {
      this.dateElement?.control?.setValue(this.validYear + '-' + newMonth + '-' + newDay);
    } else {
      // 現在の値が「XXXX-02-29」の場合、ユーザーが年を入力し、日を意図的に「00」と入力すると、
      // 入力した年がうるう年である場合、日が「29」に変換される。
      // 入力した年がうるう年ではない場合、日が「01」に変換される。
      if (newMonth === '02' && newDay === '29') {
        if (new Date(newYear + '-' + newMonth + '-' + newDay).getDate() !== 29) {
          newDay = '01';
        }
      }
      this.dateElement?.control?.setValue(newYear + '-' + newMonth + '-' + newDay);
    }
    values = this.dateElement?.control?.value.split('-');

    if (this.isDay27Only && isInput00 && values[2] !== '27') {
      this.dateElement?.control?.setValue(values[0] + '-' + values[1] + '-' + '27');
      values = this.dateElement?.control?.value.split('-');
    }
    this.validYear = Number(values[0]);
    this.validMonth = values[1];
    this.validDay = values[2];
  }

  private updateCSS() {
    if (this.isDashDisplay) {
      // デフォルトとして入力データ型が「date」の場合、年/月/日の形式で表示する。
      // その為、年-月-日の形式で表示する為にCSSを追加した。
      if (this.el.nativeElement.value || (this.validDay && this.validMonth && this.validYear > 0)) {
        this.renderer.addClass(this.el.nativeElement, 'input-ymd-has-value');
        this.renderer.removeClass(this.el.nativeElement, 'input-ymd-no-value');
      } else {
        this.renderer.removeClass(this.el.nativeElement, 'input-ymd-has-value');
        this.renderer.addClass(this.el.nativeElement, 'input-ymd-no-value');
      }
      if (this.dateElement.disabled) {
        // 入力項目が非活性の場合、「-」の背景色を変更する。
        this.renderer.addClass(this.el.nativeElement, 'input-ymd-disabled');
      } else {
        this.renderer.removeClass(this.el.nativeElement, 'input-ymd-disabled');
      }
    }
  }

  private resetKeys(): void {
    this.isSkipSetDefault = false;
    this.groupChanged = '';
    this.keys.length = 0;
    this.keys.push('0');
  }

  private getCurrentYMD(): string {
    const now = new Date();
    const year = now.getFullYear().toString().padStart(4, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const date = now.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${date}`;
  }
}

export const MAT_MOMENT_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
